export interface DatasourceOption {
  dataset_name: string;
  table_name: string;
  table_description: string;
  Alias_Query: string | null;
  business_rules: string | null;
  is_restricted: boolean;
  restriction_condition: string | null;
}

export type ColumnAggregation = 'sum' | 'average' | 'none' | 'disabled';

export interface DatasetColumn {
  key: string;
  name: string;
  alias: string;
  Formatted_Column: string | null;
  type: 'Text' | 'Number';
  description: string;
  data_type: string;
  DataType_Category: string | null;
  is_primary_key: boolean;
  is_foreign_key: boolean;
  is_restricted: boolean;
  restriction_condition: string | null;
  sample_values: string;
  synonyms: string | null;
  constraints_info: string | null;
  business_rules: string | null;
  // Parsed out of business_rules when it's non-null (e.g. '{"filterColumn":{...},"UniqueColumnsForJoin":[...]}').
  filterColumn: Record<string, string> | null;
  uniqueColumnsForJoin: string[] | null;
  Alias_Query: string | null;
  dataset_name: string;
  table_name: string;
  selected: boolean;
  aggregation: ColumnAggregation;
}

export interface DatasetConfig {
  id: string;
  name: string;
  dataset_name: string;
  table_name: string;
  table_description: string;
  Alias_Query: string | null;
  business_rules: string | null;
  is_restricted: boolean;
  restriction_condition: string | null;
  isPrimary: boolean;
  manageColumnsOpen: boolean;
  columnsLoading: boolean;
  columnsLoaded: boolean;
  columns: DatasetColumn[];
  relationshipsOpen: boolean;
  joins: DatasetJoin[];
}

export type JoinType = 'INNER JOIN' | 'LEFT JOIN' | 'RIGHT JOIN' | 'FULL OUTER JOIN' | 'CROSS JOIN';

export interface JoinTypeOption {
  value: JoinType;
  label: string;
}

export const JOIN_TYPE_OPTIONS: JoinTypeOption[] = [
  { value: 'INNER JOIN', label: 'Keep only matching records from both datasets' },
  { value: 'LEFT JOIN', label: 'Keep all rows from Dataset 1' },
  { value: 'RIGHT JOIN', label: 'Keep all rows from Dataset 2' },
  { value: 'FULL OUTER JOIN', label: 'Keep all records from both datasets' },
  { value: 'CROSS JOIN', label: 'Create all possible combinations of records' }
];

export interface ColumnMapping {
  id: string;
  leftColumn: string | null;
  rightColumn: string | null;
  // UI-only search terms for the column dropdowns, not sent in the save payload.
  leftColumnSearch?: string;
  rightColumnSearch?: string;
}

export interface DatasetJoin {
  id: string;
  targetDatasetId: string | null;
  joinType: JoinType | null;
  columnMappings: ColumnMapping[];
}

export interface SelectDatasourceSubmitPayload {
  datasets: Array<{
    id: string;
    name: string;
    table_name: string;
    table_description: string;
    dataset_name: string;
    Alias_Query: string | null;
    business_rules: string | null;
    is_restricted: boolean;
    restriction_condition: string | null;
    isPrimary: boolean;
    // Every column field is stored (not just a subset) so table/column metadata round-trips in full.
    columns: DatasetColumn[];
    joins: Array<{
      targetDatasetId: string | null;
      joinType: JoinType | null;
      columnMappings: Array<{ leftColumn: string | null; rightColumn: string | null }>;
    }>;
  }>;
}

const NUMERIC_DATA_TYPES = ['INT', 'INTEGER', 'BIGINT', 'SMALLINT', 'TINYINT', 'DECIMAL', 'NUMERIC', 'FLOAT', 'DOUBLE', 'REAL'];

export function isNumericDataType(dataType: string | null | undefined): boolean {
  return NUMERIC_DATA_TYPES.includes((dataType || '').toUpperCase());
}

// Parses the business_rules JSON string (e.g. '{"filterColumn":{"App_Filter":"ASK_GLOBAL_ID"},
// "UniqueColumnsForJoin":["ASK_GLOBAL_ID"]}') into the two keys the UI/auto-join logic cares about.
// Returns nulls for a missing/invalid value so callers don't need their own try/catch.
function parseColumnBusinessRules(rawBusinessRules: string | null | undefined): {
  filterColumn: Record<string, string> | null;
  uniqueColumnsForJoin: string[] | null;
} {
  if (!rawBusinessRules) {
    return { filterColumn: null, uniqueColumnsForJoin: null };
  }
  try {
    const parsed = JSON.parse(rawBusinessRules);
    const filterColumn = parsed?.filterColumn && typeof parsed.filterColumn === 'object' ? parsed.filterColumn : null;
    const uniqueColumnsForJoin = Array.isArray(parsed?.UniqueColumnsForJoin)
      ? parsed.UniqueColumnsForJoin.map((value: any) => String(value))
      : null;
    return { filterColumn, uniqueColumnsForJoin };
  } catch {
    return { filterColumn: null, uniqueColumnsForJoin: null };
  }
}

// Maps a raw manage-columns API record into the shape used by the UI/payload.
export function mapApiColumn(raw: any): DatasetColumn {
  const isNumber = isNumericDataType(raw?.data_type);
  const businessRules = raw?.business_rules ?? null;
  const { filterColumn, uniqueColumnsForJoin } = parseColumnBusinessRules(businessRules);
  return {
    key: raw?.column_name,
    name: raw?.column_name,
    alias: raw?.alias,
    Formatted_Column: raw?.Formatted_Column ?? null,
    type: isNumber ? 'Number' : 'Text',
    description: raw?.column_description,
    data_type: raw?.data_type,
    DataType_Category: raw?.DataType_Category ?? null,
    is_primary_key: !!raw?.is_primary_key,
    is_foreign_key: !!raw?.is_foreign_key,
    is_restricted: !!raw?.is_restricted,
    restriction_condition: raw?.restriction_condition ?? null,
    sample_values: raw?.sample_values,
    synonyms: raw?.synonyms ?? null,
    constraints_info: raw?.constraints_info ?? null,
    business_rules: businessRules,
    filterColumn,
    uniqueColumnsForJoin,
    Alias_Query: raw?.Alias_Query ?? null,
    dataset_name: raw?.dataset_name ?? '',
    table_name: raw?.table_name,
    selected: false,
    aggregation: isNumber ? 'none' : 'disabled'
  };
}

// LEFT/RIGHT JOIN keep "Dataset 1"/"Dataset 2" wording from JOIN_TYPE_OPTIONS but swap in the actual table names.
export function buildJoinTypeOptions(targetDatasetName: string, currentDatasetName: string): JoinTypeOption[] {
  const target = targetDatasetName || 'Dataset 1';
  const current = currentDatasetName || 'Dataset 2';
  return JOIN_TYPE_OPTIONS.map(option => {
    if (option.value === 'LEFT JOIN') {
      return { ...option, label: `Keep all rows from ${target}` };
    }
    if (option.value === 'RIGHT JOIN') {
      return { ...option, label: `Keep all rows from ${current}` };
    }
    return option;
  });
}

function normalizeColumnToken(value: string | null | undefined): string {
  return (value || '').toString().trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Checked/matched in THIS order everywhere a column needs to be matched to its synonym/equivalent.
const SYNONYM_PRIORITY_FIELDS: Array<'name' | 'alias' | 'synonyms' | 'Formatted_Column'> = ['name', 'alias', 'synonyms', 'Formatted_Column'];

function getColumnTokens(column: DatasetColumn): Set<string> {
  return new Set(
    SYNONYM_PRIORITY_FIELDS.flatMap(field => (column[field] || '').split(',')).map(normalizeColumnToken).filter(Boolean)
  );
}

// Two columns can be mapped together if they share a name/alias/synonym/Formatted_Column once
// punctuation and casing are stripped (e.g. "ASK_GLOBAL_ID" and "ask global id" both normalize to "askglobalid").
export function areColumnsSynonymous(left: DatasetColumn | undefined, right: DatasetColumn | undefined): boolean {
  if (!left || !right) {
    return false;
  }
  const leftTokens = getColumnTokens(left);
  const rightTokens = getColumnTokens(right);
  for (const token of leftTokens) {
    if (rightTokens.has(token)) {
      return true;
    }
  }
  return false;
}

// Auto-selection: find the candidate whose name/alias/synonyms/Formatted_Column matches `source`'s,
// checking source's own fields in that SAME priority order (first field with any match wins).
export function findSynonymColumn(source: DatasetColumn, candidates: DatasetColumn[]): DatasetColumn | undefined {
  for (const field of SYNONYM_PRIORITY_FIELDS) {
    const tokens = (source[field] || '').split(',').map(normalizeColumnToken).filter(Boolean);
    if (!tokens.length) {
      continue;
    }
    const match = candidates.find(candidate => {
      const candidateTokens = getColumnTokens(candidate);
      return tokens.some(token => candidateTokens.has(token));
    });
    if (match) {
      return match;
    }
  }
  return undefined;
}

// A column from the SAME dataset's `uniqueColumnsForJoin` list (parsed from business_rules) is also
// treated as a join key, in addition to `is_primary_key` - either marks it as a candidate for auto-join.
function isJoinKeyColumn(column: DatasetColumn, datasetColumns: DatasetColumn[]): boolean {
  if (column.is_primary_key) {
    return true;
  }
  const uniqueNames = new Set(
    (datasetColumns || []).flatMap(candidate => candidate.uniqueColumnsForJoin || []).map(normalizeColumnToken)
  );
  return uniqueNames.has(normalizeColumnToken(column.name)) || uniqueNames.has(normalizeColumnToken(column.key));
}

// Auto-join: pairs every join-key column (is_primary_key or listed in a uniqueColumnsForJoin, on either
// side) with its synonymous column on the other table. Pairs are returned as target (left) / own (right)
// and are de-duplicated - if primary-key and uniqueColumnsForJoin resolve to the same pair, only one
// mapping is added. A key column with no counterpart is skipped because a half-filled mapping is invalid.
export function buildPrimaryKeyMappingPairs(
  targetColumns: DatasetColumn[],
  ownColumns: DatasetColumn[]
): Array<{ leftColumn: string; rightColumn: string }> {
  const pairs = new Map<string, { leftColumn: string; rightColumn: string }>();
  const addPair = (left: DatasetColumn | undefined, right: DatasetColumn | undefined): void => {
    if (left?.key && right?.key) {
      pairs.set(`${left.key}\u0000${right.key}`, { leftColumn: left.key, rightColumn: right.key });
    }
  };

  (targetColumns || []).filter(column => isJoinKeyColumn(column, targetColumns)).forEach(left => addPair(left, findSynonymColumn(left, ownColumns || [])));
  (ownColumns || []).filter(column => isJoinKeyColumn(column, ownColumns)).forEach(right => addPair(findSynonymColumn(right, targetColumns || []), right));

  return [...pairs.values()];
}

export function findDatasetColumn(dataset: DatasetConfig | undefined, key: string | null): DatasetColumn | undefined {
  if (!dataset || !key) {
    return undefined;
  }
  return dataset.columns.find(column => column.key === key);
}

export function isColumnMappingValid(
  mapping: ColumnMapping,
  targetDataset: DatasetConfig | undefined,
  ownDataset: DatasetConfig | undefined
): boolean {
  if (!mapping.leftColumn || !mapping.rightColumn) {
    return false;
  }
  return areColumnsSynonymous(
    findDatasetColumn(targetDataset, mapping.leftColumn),
    findDatasetColumn(ownDataset, mapping.rightColumn)
  );
}

// A join is fully configured once a target + join type are chosen and at least one valid (synonymous) column mapping exists.
export function isJoinFullyConfigured(join: DatasetJoin, ownDataset: DatasetConfig, allDatasets: DatasetConfig[]): boolean {
  if (!join.targetDatasetId || !join.joinType) {
    return false;
  }
  const target = allDatasets.find(dataset => dataset.id === join.targetDatasetId);
  return join.columnMappings.some(mapping => isColumnMappingValid(mapping, target, ownDataset));
}

// Given a selected "Label" column's composite key (`${datasetId}.${columnKey}`, the addressing scheme
// used by the add-filter/add-visual column pickers) and the raw datasource JSON, resolves the "Value"
// column key that should be paired with it: if the label column's filterColumn (parsed from
// business_rules, e.g. {"App_Filter":"ASK_GLOBAL_ID"}) names another column in the same dataset, that
// column's key is returned; otherwise the label's own key is returned (same-column fallback). Used
// uniformly whether the label was picked from the data column list, the side "manage properties" panel,
// or resolved from an ASK-generated filter.
export function resolveFilterValueColumnKey(labelKey: string | null | undefined, dataSourceJson: any): string {
  if (!labelKey) {
    return labelKey || '';
  }
  let parsed = dataSourceJson;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { return labelKey; }
  }
  const separatorIndex = labelKey.indexOf('.');
  if (separatorIndex < 0) {
    return labelKey;
  }
  const datasetId = labelKey.slice(0, separatorIndex);
  const columnKey = labelKey.slice(separatorIndex + 1);
  const datasets: any[] = Array.isArray(parsed?.datasets) ? parsed.datasets : [];
  const dataset = datasets.find(ds => String(ds?.id ?? '') === datasetId);
  const columns: any[] = Array.isArray(dataset?.columns) ? dataset.columns : [];
  const labelColumn = columns.find(col => String(col?.key ?? '') === columnKey);
  const filterColumn = labelColumn?.filterColumn;
  if (!filterColumn || typeof filterColumn !== 'object') {
    return labelKey;
  }
  const targetName = Object.values(filterColumn)
    .map(value => String(value ?? '').trim())
    .find(Boolean);
  if (!targetName) {
    return labelKey;
  }
  const normalizedTarget = normalizeColumnToken(targetName);
  const match = columns.find(col =>
    normalizeColumnToken(col?.key) === normalizedTarget ||
    normalizeColumnToken(col?.name) === normalizedTarget ||
    normalizeColumnToken(col?.alias) === normalizedTarget
  );
  return match ? `${datasetId}.${match.key}` : labelKey;
}

// Case-insensitive check used when naming a new calculated column - it must not collide with an existing one.
export function isDuplicateColumnName(columns: DatasetColumn[], name: string): boolean {
  const normalized = (name || '').trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return columns.some(column => (column.name || '').trim().toLowerCase() === normalized);
}

// Case-insensitive check used on the formula/expression field - it must not just restate an existing
// column's alias (i.e. duplicate an already-available calculation under a new name).
export function isDuplicateColumnAlias(columns: DatasetColumn[], expression: string): boolean {
  const normalized = (expression || '').trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return columns.some(column => (column.alias || '').trim().toLowerCase() === normalized);
}

type ExpressionOperandType = 'Number' | 'Text' | null;

interface ExpressionOperand {
  label: string;
  type: ExpressionOperandType;
}

const ARITHMETIC_OPERATORS = new Set(['+', '-', '*', '/']);

// Matches, in order: a SUM(...) call (capturing its inner expression), a single arithmetic operator,
// an identifier (optionally table-qualified, e.g. "A.Curr_Year_FC_Curr_Charges"), or a numeric literal.
const EXPRESSION_TOKEN_PATTERN = /SUM\s*\(\s*([^)]*?)\s*\)|([+\-*/])|([A-Za-z_][\w.]*)|(\d+(?:\.\d+)?)/gi;

function resolveExpressionColumn(identifier: string, columns: DatasetColumn[]): DatasetColumn | undefined {
  const segments = identifier.split('.');
  const name = normalizeColumnToken(segments[segments.length - 1]);
  if (!name) {
    return undefined;
  }
  return columns.find(column =>
    normalizeColumnToken(column.name) === name ||
    normalizeColumnToken(column.key) === name ||
    normalizeColumnToken(column.alias) === name
  );
}

// Validates the arithmetic in a calculated-column expression:
//  - SUM(...) must wrap a numeric column (a text column inside SUM is invalid).
//  - +, -, * and / between two resolvable columns require both to be the same data type.
//  - -, * and / additionally require that shared type to be numeric (two text columns can't be
//    subtracted/multiplied/divided, though + is still allowed between them as string concatenation).
// Tokens that can't be resolved to a known column (functions, literals, etc.) are skipped rather than
// flagged, since this is a best-effort heuristic check, not a full expression parser.
export function validateCalculatedColumnExpression(expression: string, columns: DatasetColumn[]): string | null {
  const trimmed = (expression || '').trim();
  if (!trimmed) {
    return null;
  }

  const tokens: Array<{ kind: 'sum' | 'operator' | 'identifier' | 'number'; value: string }> = [];
  let match: RegExpExecArray | null;
  EXPRESSION_TOKEN_PATTERN.lastIndex = 0;
  while ((match = EXPRESSION_TOKEN_PATTERN.exec(trimmed)) !== null) {
    if (match[1] !== undefined) {
      tokens.push({ kind: 'sum', value: match[1].trim() });
    } else if (match[2]) {
      tokens.push({ kind: 'operator', value: match[2] });
    } else if (match[3]) {
      tokens.push({ kind: 'identifier', value: match[3] });
    } else if (match[4]) {
      tokens.push({ kind: 'number', value: match[4] });
    }
  }

  // A SUM(...) must wrap a numeric column.
  for (const token of tokens) {
    if (token.kind !== 'sum' || !token.value) {
      continue;
    }
    const column = resolveExpressionColumn(token.value, columns);
    if (column && column.type !== 'Number') {
      return `SUM(${token.value}) is not valid - "${column.name}" is a text column and can't be summed.`;
    }
  }

  const operandType = (token: { kind: string; value: string }): ExpressionOperand => {
    if (token.kind === 'number') {
      return { label: token.value, type: 'Number' };
    }
    const column = resolveExpressionColumn(token.value, columns);
    return { label: token.kind === 'sum' ? `SUM(${token.value})` : token.value, type: column ? column.type : null };
  };

  let left: ExpressionOperand | null = null;
  let pendingOperator: string | null = null;
  for (const token of tokens) {
    if (token.kind === 'operator') {
      pendingOperator = ARITHMETIC_OPERATORS.has(token.value) ? token.value : null;
      continue;
    }

    const operand = operandType(token);
    if (left && pendingOperator && left.type && operand.type) {
      if (left.type !== operand.type) {
        return `Cannot use "${pendingOperator}" between "${left.label}" (${left.type.toLowerCase()}) and ` +
          `"${operand.label}" (${operand.type.toLowerCase()}) - they must be the same data type.`;
      }
      if (left.type === 'Text' && pendingOperator !== '+') {
        return `Cannot use "${pendingOperator}" on text columns "${left.label}" and "${operand.label}" - ` +
          'only numeric columns can be subtracted, multiplied or divided.';
      }
    }
    left = operand;
    pendingOperator = null;
  }

  return null;
}
